import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, MessageCircle } from "lucide-react";
import Logo from "@/components/common/Logo";
import { NAV, COMPANY } from "@/data/site";
import { useQuote } from "@/context/QuoteContext";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { openQuote } = useQuote();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 w-full z-50 transition-colors duration-300 ${
        scrolled ? "bg-black/80 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="container-gema flex items-center justify-between h-20">
        <Link to="/" data-testid="logo-link" onClick={() => setOpen(false)}>
          <Logo />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8" aria-label="Principal">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative font-heading text-sm font-bold uppercase tracking-wider transition-colors duration-300 ${
                  isActive ? "text-gema-red" : "text-white/80 hover:text-white"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={COMPANY.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp"
            className="flex items-center gap-2 text-white/80 hover:text-white transition-colors duration-300 text-sm font-medium"
          >
            <MessageCircle className="h-4 w-4" /> WhatsApp
          </a>
          <button data-testid="header-quote-btn" onClick={() => openQuote()} className="btn-primary py-3 px-6">
            Solicitar Cotización
          </button>
        </div>

        {/* Mobile toggle */}
        <button
          className="lg:hidden text-white p-2"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Abrir menú"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden fixed inset-0 top-20 bg-gema-black transition-transform duration-300 ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        data-testid="mobile-menu"
      >
        <nav className="flex flex-col p-8 gap-2">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `py-4 border-b border-white/10 font-heading text-2xl font-black uppercase ${
                  isActive ? "text-gema-red" : "text-white"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <button
            data-testid="mobile-quote-btn"
            onClick={() => { setOpen(false); openQuote(); }}
            className="btn-primary mt-6"
          >
            Solicitar Cotización
          </button>
          <a href={COMPANY.whatsapp} target="_blank" rel="noopener noreferrer" className="btn-outline-light mt-3">
            <MessageCircle className="h-4 w-4" /> Contactar por WhatsApp
          </a>
        </nav>
      </div>
    </header>
  );
}
